import React from 'react';

import {Link} from 'react-router-dom';

import {CheckCircleOutlined, CloseCircleOutlined, LoadingOutlined} from '@ant-design/icons';
import {Tooltip, Typography} from 'antd';
import Big from 'big.js';
import humanize from 'humanize-string';
import {friendlyFormatIBAN} from 'ibantools';
import type {CountryCode} from 'libphonenumber-js';
import {parsePhoneNumberFromString} from 'libphonenumber-js';
import {divide} from 'mathjs';
import type {MomentFormatSpecification} from 'moment';
import moment from 'moment-timezone';
import {stringify} from 'querystring';
import {encodeQueryParams} from 'use-query-params';

import {amountToInt, dateFormat, truncate as truncateUrl} from './utils';

import type {Address} from 'api/generated';
import {AccountInvoicePaymentMethod, Currencies} from 'api/generated';
import countries from 'assets/countries.json';
import {AnchorLink} from 'components';
import {EmailLink} from 'components/EmailLink';
import {queryFiltersSchema} from 'routes/Payments/Payments.utils';

const {Text} = Typography;

export const formatWebsite = (website?: string | null, truncate?: boolean) => {
  if (!website) return null;
  if (!website.startsWith('http')) {
    website = `https://${website}`;
  }
  const hostname = /\/\/([^/]+)/.exec(website)?.[1];
  return (
    <AnchorLink href={website} external>
      {truncate ? truncateUrl(hostname, 25) : hostname}
    </AnchorLink>
  );
};

export const formatPhone = (
  phone?: string | null,
  countryCode?: string | null,
  asLink?: boolean
) => {
  if (!phone) return null;
  const tel = parsePhoneNumberFromString(phone, countryCode as CountryCode)?.formatInternational();
  if (!asLink) return tel;
  return (
    <AnchorLink href={`tel:${tel ?? ''}`} external>
      {tel}
    </AnchorLink>
  );
};

export const formatGender = (gender?: string | null) => {
  switch (gender) {
    case 'M':
      return 'Male';
    case 'F':
      return 'Female';
  }
  return null;
};

export const formatIBAN = (iban?: string | null, last4?: boolean) => {
  if (!iban) return null;
  if (last4) return `•••• ${iban.slice(-4)}`;
  return friendlyFormatIBAN(iban);
};

export const formatEmail = (email?: string | null, truncate?: boolean) => {
  if (!email) return null;
  return <EmailLink email={email} truncate={truncate} />;
};

export const formatDate = (
  date: string | number | null | undefined,
  format: string | MomentFormatSpecification = 'YYYY-MM-DD',
  showRelative = false,
  showTime = false,
  timezone: string = moment.tz.guess()
) => {
  if (!date) return null;

  // Create a moment in the desired timezone. Prefer robust parsing for ISO strings.
  const isISO = typeof date === 'string' && /\d{4}-\d{2}-\d{2}T/.test(date);

  const momentDate = (() => {
    if (typeof date === 'number') return moment.unix(date).tz(timezone);
    if (isISO) return moment.tz(date, timezone);
    if (format === 'X') return moment.unix(Number(date)).tz(timezone);
    return moment.tz(date, format, timezone);
  })();

  const result = momentDate.format(showTime ? 'LLL' : 'LL');
  return showRelative ? `${result} (${momentDate.fromNow()})` : result;
};

export const formatRelativeDate = (date?: string | null) => {
  if (!date) return undefined;
  const anHourAgo = moment().subtract(59, 'minutes');
  const fullDate = moment(date).format('lll');
  const relativeDate = moment.max(moment(date), anHourAgo).isSame(anHourAgo)
    ? moment(date).format(dateFormat.dateTime)
    : moment(date).fromNow();
  return (
    <Tooltip placement="topLeft" title={fullDate}>
      {relativeDate}
    </Tooltip>
  );
};

export const formatHumanString = (str?: string | null) => {
  if (!str) return null;
  return humanize(str);
};

export const formatHumanName = (name?: string | null) => {
  if (!name) return null;
  return capitalize(humanize(name));
};

export const capitalize = (str: string) => {
  return str
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const formatError = (error?: string | null) => {
  if (!error) return null;
  return (
    <Text type="danger" strong>
      <CloseCircleOutlined /> {error.split('. ').map(humanize).join('. ')}
    </Text>
  );
};

export const formatAddress = (address?: Address | null) => {
  if (!address) return null;
  const {line1, line2, city, country, state, zip} = address;
  const lines: React.ReactNode[] = [];
  if (line1) lines.push(line1);
  if (line2) lines.push(line2);
  if (city || state || zip || country) {
    const line: string[] = [];
    if (city) line.push(city);
    if (state) line.push(state);
    if (zip) line.push(zip);
    if (country) {
      line.push((countries as Record<string, string>)[country] || country);
    }
    lines.push(line.join(', '));
  }
  if (lines.length === 0) return null;
  const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    lines.map((line) => (typeof line === 'string' ? line : JSON.stringify(line))).join(' ')
  )}`;
  const result = lines.reduce<React.ReactNode[]>((acc, line, index) => {
    if (index === 0) return [line];
    return [...acc, <br key={index} />, line];
  }, []);
  return (
    <AnchorLink href={url} target="_blank" rel="noreferrer">
      {result}
    </AnchorLink>
  );
};

export const formatLanguage = (language?: string | null) => {
  switch (language) {
    case 'en':
      return 'English';
    case 'es':
      return 'Spanish';
    case 'ca':
      return 'Catalan';
    default:
      return language;
  }
};

export const formatAmount = (
  amount: number | undefined | null,
  currency?: string | null,
  precision = 0,
  returnNull = false
) => {
  if (!amount) return returnNull ? null : 0;
  if (!currency) currency = Currencies.EUR;
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
    minimumFractionDigits: precision,
    maximumFractionDigits: precision
  }).format(amount);
};

export const formatIntAmount = (
  amount: number | undefined | null,
  currency?: string | null,
  precision = 2
) => {
  if (!amount) amount = 0;
  if (!currency) currency = Currencies.EUR;
  return formatAmount(divide(amount, 100), currency, precision);
};

export const formatApiKey = (key: string) => {
  if (!key) return '';
  return '•'.repeat(key.length);
};

export const formatPercent = (value: number | undefined | null) => {
  if (!value) value = 0;
  return new Intl.NumberFormat(undefined, {
    style: 'percent',
    maximumFractionDigits: 2
  }).format(value / 100);
};

export const formatBoolean = (
  isTrue?: boolean | null,
  enabledLabel = 'Yes',
  disabledLabel = 'No'
) => {
  return isTrue ? (
    <Text type="success" strong>
      <CheckCircleOutlined /> <span>{enabledLabel}</span>
    </Text>
  ) : (
    <Text type="danger" strong>
      <CloseCircleOutlined /> <span>{disabledLabel}</span>
    </Text>
  );
};

export const formatReferralLink = (link?: string | null) => {
  if (!link) return null;
  try {
    const url = new URL(link);
    return url.searchParams.get('ref');
  } catch (error) {
    console.error('Invalid URL format:', error);
    return null;
  }
};

export const formatList = <T,>(
  list: T[] | null | undefined,
  format: (item: T) => React.ReactNode
) => {
  if (!list || list.length === 0) return null;
  return (
    <ul>
      {list.map((item, i) => (
        <li key={i}>{format(item)}</li>
      ))}
    </ul>
  );
};

export const formatTable = <T extends Array<unknown>>(
  list: T | null | undefined,
  format: (item: T[number]) => React.ReactNode[]
) => {
  if (!list || list.length === 0) return null;
  return (
    <table className="simple-table">
      <tbody>
        {list.map((item, i) => (
          <tr key={i}>
            {format(item).map((part, i) => (
              <td key={i}>{part}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export const formatUrl = (url?: string | null, name?: string | null) => {
  if (!url) return null;
  return (
    <AnchorLink href={url} external>
      {name || url}
    </AnchorLink>
  );
};

export const formatDateRange = (from: number, to: number, format = 'LL') => {
  return `${moment.unix(from).utc().format(format)} - ${moment.unix(to).utc().format(format)}`;
};

export const formatInvoicePaymentMethod = (paymentMethod?: AccountInvoicePaymentMethod | null) => {
  if (!paymentMethod) return 'Direct debit';
  switch (paymentMethod) {
    case AccountInvoicePaymentMethod.BANK_TRANSFER:
      return 'Bank transfer';
    default:
      return 'Direct debit';
  }
};

export const formatNumber = (value: number | undefined | null) => {
  if (!value) value = 0;
  return new Intl.NumberFormat(undefined).format(value);
};

export const buildCardNote = (payment: {
  paymentMethod?: {
    card?: {brand?: string | null; type?: string | null; last4?: string | null} | null;
    cardPresent?: {brand?: string | null; type?: string | null; last4?: string | null} | null;
  } | null;
}): string | undefined => {
  const card = payment.paymentMethod?.card || payment.paymentMethod?.cardPresent;
  if (!card?.last4) return undefined;
  return [
    card.brand && capitalize(card.brand),
    card.type && capitalize(card.type),
    `•••• ${card.last4}`
  ]
    .filter(Boolean)
    .join(' ');
};

export const buildSessionNote = (
  payment: {
    sessionDetails?: {
      browser?: string | null;
      os?: string | null;
      countryCode?: string | null;
    } | null;
  },
  countryName?: string | null
): string | undefined => {
  const country = countryName || payment.sessionDetails?.countryCode;
  const parts = [
    payment.sessionDetails?.browser,
    payment.sessionDetails?.os && `on ${payment.sessionDetails.os}`,
    country && `(${country})`
  ].filter(Boolean);
  return parts.join(' ') || undefined;
};

export const getLast3MonthsUnixRange = (): [number, number] => [
  moment().subtract(3, 'months').startOf('day').unix(),
  moment().endOf('day').unix()
];

export const getBlacklistPaymentsUrl = (
  filterKey: string,
  value: string,
  accountId?: string
): string => {
  const queryParams = {
    [filterKey]: value,
    createdAt: [moment().subtract(3, 'months').format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')],
    ...(accountId ? {accountId} : {})
  };
  return (
    '/payments?' +
    stringify(
      encodeQueryParams(queryFiltersSchema, queryParams) as Record<
        string,
        string | number | boolean | null
      >
    )
  );
};

export const getShopLink = (shopName?: string | null) => {
  if (!shopName) return null;
  const queryParams = {
    shopName: shopName.split(' | ')[0]
  };
  return (
    <Link
      to={{
        pathname: '/payments',
        search: stringify(
          encodeQueryParams(queryFiltersSchema, queryParams) as Record<
            string,
            string | number | boolean | null
          >
        )
      }}>
      {shopName}
    </Link>
  );
};

export const getRelativeTime = (date: number) => {
  const anHourAgo = moment().subtract(59, 'minutes');
  return moment.max(moment.unix(date), anHourAgo).isSame(anHourAgo)
    ? moment.unix(date).format(dateFormat.dateTime)
    : moment.unix(date).fromNow();
};

export const removeSpecialChars = (value: string) => {
  return value.replace(/[[\]`·~!@#$%^&*()-+{}|<>"'=+;:ªº¨/?]/g, '');
};

export const formatUnixDate = (
  date: number | null | undefined,
  fromNow = false,
  format: string = 'lll',
  timezone: string = moment.tz.guess()
) => {
  if (!date) return null;
  const m = moment.unix(date).tz(timezone);
  return fromNow ? m.fromNow() : m.format(format);
};

export const inputNumberFormatter = (value: string | number | undefined | null) => {
  const amount = new Intl.NumberFormat(undefined, {
    style: 'decimal',
    maximumFractionDigits: 2
  }).format(
    Big(value ?? 0)
      .div(100)
      .toNumber()
  );
  return amount.replace('.', ',');
};

export const inputNumberParser = (value: string | undefined | null) => {
  const cleanValue = value
    ? removeSpecialChars(value.replace(/[a-zA-Z]/g, '')).replace(',', '.')
    : '0';
  return amountToInt(Number(cleanValue));
};

export const formatSignUpLink = (promoCode?: string | null) => {
  if (!promoCode) return 'https://dashboard.monei.com/?action=signUp';
  return `https://dashboard.monei.com/?action=signUp&promo=${promoCode}`;
};

export const formatAffiliateReferralCode = (
  referralCode: string | null | undefined,
  affiliates: Array<{value: string; label: string; id: string}>,
  affiliatesLoading: boolean
) => {
  if (!referralCode) return null;

  if (affiliatesLoading) {
    return (
      <Text>
        <LoadingOutlined />
      </Text>
    );
  }

  const affiliate = affiliates.find((a) => a.value === referralCode);
  if (!affiliate) return referralCode;

  return <Link to={`/affiliates/${affiliate.id}`}>{affiliate.label}</Link>;
};
